import React from 'react';
import { FileSpreadsheet } from 'lucide-react';
import type { SearchOptionType } from '@generated';
import { Button } from '~/shared/components/buttons/Button';
import { Modal } from '~/shared/components/modal/Modal';
import { exportTable, type ExportTableArgs } from './exportTable';

export type ExportButtonProps<T extends SearchOptionType> = Omit<ExportTableArgs<T>, 'exportAll'>;

export function ExportButton<T extends SearchOptionType>(props: ExportButtonProps<T>) {
	return (
		<Button
			label="Export"
			Icon={FileSpreadsheet}
			appearance="outline"
			size="sm"
			renderModal={closeModal => <ExportModal {...props} closeModal={closeModal} />}
		/>
	);
}

function ExportModal<T extends SearchOptionType>({ closeModal, ...args }: ExportButtonProps<T> & { closeModal: () => void }) {
	const [pending, setPending] = React.useState<'page' | 'all' | null>(null);
	const [hasError, setHasError] = React.useState(false);

	const runExport = async (exportAll: boolean) => {
		setPending(exportAll ? 'all' : 'page');
		setHasError(false);
		try {
			await exportTable({ ...args, exportAll });
			closeModal();
		} catch {
			setHasError(true);
		} finally {
			setPending(null);
		}
	};

	return (
		<Modal title="Export to Excel" closeModal={closeModal}>
			<p>Do you want to export the current view or all results?</p>
			{hasError && <p className="mt-2 text-sm text-red-600">Export failed. Please try again.</p>}
			<div className="mt-6 flex justify-end gap-2">
				<Button
					appearance="outline"
					label="Current view"
					onClick={() => runExport(false)}
					isPending={pending === 'page'}
					isDisabled={pending !== null}
				/>
				<Button label="All results" onClick={() => runExport(true)} isPending={pending === 'all'} isDisabled={pending !== null} />
			</div>
		</Modal>
	);
}
